import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} SellingPlan
 * @property {number} id
 * @property {string} name
 * @property {number} groupIndex
 * @property {string} adjustmentType
 * @property {number} adjustmentValue
 */

/**
 * @typedef {Object} SellingPlanPickerRefs
 * @property {HTMLInputElement} sellingPlanInput - Hidden input for selling_plan form field
 */

/**
 * Custom element that manages selling plan selection (one-time vs subscription).
 * Updates the hidden selling_plan input and displayed prices when the purchase option changes.
 *
 * @extends {Component<SellingPlanPickerRefs>}
 */
class SellingPlanPicker extends Component {
  #abortController = new AbortController();

  /** @type {SellingPlan[]} */
  #sellingPlans = [];

  /** @type {number} */
  #originalPrice = 0;

  /** @type {number} */
  #originalComparePrice = 0;

  /** @type {string} */
  #moneyFormat = '${{amount}}';

  /** @type {string} */
  #currency = 'USD';

  connectedCallback() {
    super.connectedCallback();

    this.#originalPrice = parseInt(this.dataset.originalPrice, 10);
    this.#originalComparePrice = parseInt(this.dataset.originalComparePrice, 10) || 0;
    this.#moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.#currency = this.dataset.currency || 'USD';

    const jsonEl = this.parentElement?.querySelector('[data-selling-plans-json]');
    this.#sellingPlans = jsonEl ? JSON.parse(jsonEl.textContent) : [];

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Handles variant:update events to sync selling plan state with the new variant.
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    const variant = event.detail?.resource;

    if (!variant || !variant.available) {
      this.hidden = true;
      this.refs.sellingPlanInput.value = '';

      const oneTimeRadio = this.querySelector('.bb-sp__radio[value=""]');

      if (oneTimeRadio) {
        /** @type {HTMLInputElement} */ (oneTimeRadio).checked = true;
      }
    } else {
      this.hidden = false;
      this.#originalPrice = variant.price;
      this.#originalComparePrice = variant.compare_at_price || 0;
      this.dataset.originalPrice = variant.price;
      this.dataset.originalComparePrice = variant.compare_at_price || 0;

      // Recalculate price if a subscription is currently selected
      const checkedRadio = /** @type {HTMLInputElement | null} */ (
        this.querySelector('.bb-sp__radio:checked')
      );

      if (checkedRadio && checkedRadio.value !== '') {
        const select = checkedRadio.closest('.bb-sp__option')?.querySelector('.bb-sp__frequency-select');
        const planId = select ? parseInt(/** @type {HTMLSelectElement} */ (select).value, 10) : parseInt(checkedRadio.value, 10);
        this.#updateDisplayedPrices(planId);
      } else {
        this.#updateDisplayedPrices(null);
      }
    }
  };

  /**
   * Handles purchase option radio change (one-time vs subscription).
   * Bound via on:change="/handlePurchaseOptionChange" in the template.
   * @param {Event} event
   */
  handlePurchaseOptionChange(event) {
    const target = /** @type {HTMLInputElement} */ (event.target);
    const value = target.value;

    if (value === '') {
      this.refs.sellingPlanInput.value = '';
      this.#updateDisplayedPrices(null);
    } else {
      const label = target.closest('.bb-sp__option');
      const select = /** @type {HTMLSelectElement | null} */ (label?.querySelector('.bb-sp__frequency-select'));
      const planId = select ? parseInt(select.value, 10) : parseInt(value, 10);
      this.refs.sellingPlanInput.value = String(planId);
      this.#updateDisplayedPrices(planId);
    }
  }

  /**
   * Handles frequency select change within the subscription option.
   * Bound via on:change="/handleFrequencyChange" in the template.
   * @param {Event} event
   */
  handleFrequencyChange(event) {
    const target = /** @type {HTMLSelectElement} */ (event.target);
    const label = target.closest('.bb-sp__option');
    const radio = /** @type {HTMLInputElement | null} */ (label?.querySelector('.bb-sp__radio'));

    if (radio && !radio.checked) {
      radio.click();
    }

    const planId = parseInt(target.value, 10);
    this.refs.sellingPlanInput.value = String(planId);
    this.#updateDisplayedPrices(planId);
  }

  /**
   * Calculates the adjusted price for a given selling plan.
   * @param {number} planId
   * @returns {number} Adjusted price in cents
   */
  #getAdjustedPrice(planId) {
    const plan = this.#sellingPlans.find((p) => p.id === planId);
    if (!plan) return this.#originalPrice;

    if (plan.adjustmentType === 'percentage') {
      return Math.round(this.#originalPrice * (100 - plan.adjustmentValue) / 100);
    }

    if (plan.adjustmentType === 'fixed_amount') {
      return this.#originalPrice - plan.adjustmentValue;
    }

    return this.#originalPrice;
  }

  /**
   * Formats a price in cents using the store's money format.
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    return formatMoney(cents, this.#moneyFormat, this.#currency).replace('.00', '');
  }

  /**
   * Updates all displayed prices in the section to reflect the selected selling plan.
   * @param {number | null} planId - The selected selling plan ID, or null for one-time purchase
   */
  #updateDisplayedPrices(planId) {
    const section = this.closest('.shopify-section, dialog');
    if (!section) return;

    const newPrice = planId ? this.#getAdjustedPrice(planId) : this.#originalPrice;
    const formattedPrice = this.#formatMoney(newPrice);

    // Update add-to-cart button price
    const atcPrice = section.querySelector('.add-to-cart-text__price');
    if (atcPrice) atcPrice.textContent = formattedPrice;

    // Update add-to-cart compare-at price
    const atcPriceGroup = section.querySelector('.add-to-cart-text__price-group');

    if (atcPriceGroup) {
      let atcCompare = atcPriceGroup.querySelector('.add-to-cart-text__compare-price');

      if (planId && newPrice < this.#originalPrice) {
        const formattedOriginal = this.#formatMoney(this.#originalPrice);

        if (!atcCompare) {
          atcCompare = document.createElement('span');
          atcCompare.className = 'compare-at-price add-to-cart-text__compare-price';
          atcPriceGroup.prepend(atcCompare);
        }

        atcCompare.textContent = formattedOriginal;
      } else if (!planId && this.#originalComparePrice <= this.#originalPrice) {
        atcCompare?.remove();
      }
    }

    // Update price block
    const priceContainer = section.querySelector('product-price [ref="priceContainer"]');

    if (priceContainer) {
      const priceEl = priceContainer.querySelector('.price');
      if (priceEl) priceEl.textContent = formattedPrice;

      let compareEl = priceContainer.querySelector('.compare-at-price');

      if (planId && newPrice < this.#originalPrice) {
        const formattedOriginal = this.#formatMoney(this.#originalPrice);

        if (!compareEl) {
          const wrapper = document.createElement('span');
          wrapper.setAttribute('role', 'group');
          compareEl = document.createElement('span');
          compareEl.className = 'compare-at-price';
          wrapper.appendChild(compareEl);
          priceContainer.prepend(wrapper);
        }

        compareEl.textContent = formattedOriginal;
      } else if (!planId && this.#originalComparePrice <= this.#originalPrice) {
        const wrapper = compareEl?.closest('[role="group"]');

        if (wrapper) {
          wrapper.remove();
        } else {
          compareEl?.remove();
        }
      }
    }
  }
}

if (!customElements.get('selling-plan-picker')) {
  customElements.define('selling-plan-picker', SellingPlanPicker);
}
